import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import "./convert.css";

const ASSETS = [
    { name: "Tether", hint: "1", icon: "/images/option-img/usdt_icon.svg" },
    { name: "USD Coin", hint: "0.9995", icon: "/images/convert-img/usd_coin.svg" },
    { name: "Bitcoin", hint: "77,407.12", icon: "/images/market-img/bitcoin_icon.svg" },
    { name: "Ethereum", hint: "2,314.44", icon: "/images/convert-img/ethereum.svg" },
    { name: "DOGE", hint: "0.09572/", icon: "/images/convert-img/doge_icon.svg" },
    { name: "Tron", hint: "0.3283", icon: "/images/convert-img/tron_icon.svg" },
    { name: "XRP", hint: "1.4107", icon: "/images/convert-img/xrp_icon.svg"},
    { name: "Litecoin", hint: "55.15", icon: "/images/convert-img/litecoin_icon.svg"},
    { name: "Alchemy", hint: "0.006113", icon: "/images/convert-img/alchemy_icon.svg" },
    { name: "Solana", hint: "85.43", icon: "/images/convert-img/solana_icon.svg" },
    { name: "AME Chain", hint: "0.000291", icon: "/images/convert-img/ame_chain_icon.svg" },
    {  more: true },
];

const FAQ_ITEMS = [
    {
        question: "What are the benefits of instant crypto conversion?",
        answer:
            "AGCX Convert lets you swap one asset for another at a quoted rate without placing limit orders on the spot book. You get a simple flow, fast execution, and transparent pricing—ideal when you want to move between coins quickly on AGCX.",
    },
    {
        question: "How can I convert USDT to BTC easily?",
        answer:
            "On AGCX Convert, choose USDT as the asset you spend and BTC as what you receive, enter the amount, review the live quote and confirm. AGCX matches liquidity behind the scenes and settles the conversion in one step.",
    },
    {
        question: "What is Auto-Invest and how does it work?",
        answer:
            "Auto-Invest on AGCX is a recurring plan that buys crypto for you on a schedule you set (for example weekly). It helps average into positions over time instead of timing a single large trade.",
    },
    {
        question: "How can I set up Auto-Invest?",
        answer:
            "Open the Auto-Invest tab on AGCX Convert, pick the asset and amount, choose the frequency, and confirm. You can pause or adjust the plan later from your AGCX account as long as you meet eligibility and balance requirements.",
    },
    {
        question: "How does block trading work for large orders?",
        answer:
            "For large sizes, AGCX can match you with deep liquidity so price impact and slippage are managed with a quoted rate. Availability and minimums depend on AGCX product rules and compliance in your region. Contact support@arabglobal.ae for OTC / large-size help.",
    },
    {
        question: "What are the minimum and maximum trade limits?",
        answer:
            "Limits on AGCX depend on the asset pair, your KYC verification level, and current risk settings. You will see the allowed range on the convert form before you confirm.",
    },
    {
        question: "What is the difference between Convert and Spot trading?",
        answer:
            "AGCX Convert is a simplified swap at a quoted rate. AGCX Spot trading uses the order book with limit and market orders, charts, and full control over price. Use Convert for speed; use Spot when you need precise pricing and order types.",
    },
    {
        question: "When will the converted assets be credited?",
        answer:
            "After a successful AGCX conversion, balances usually update within a short processing window. Your AGCX wallet and order history will show the completed transaction once settlement finishes.",
    },
];

function AssetIcon({ asset }) {
    if (asset.more) {
        return (
            <span className="convert-page__assetIcon convert-page__assetIcon--more" aria-hidden>
                ···
            </span>
        );
    }
    if (asset.icon) {
        return <img className="convert-page__assetIconImg" src={asset.icon} alt="" />;
    }
    return (
        <span className="convert-page__assetIcon convert-page__assetIcon--letter" style={{ background: asset.color }}>
            {asset.letter}
        </span>
    );
}

export default function ConvertPage() {
    const [tab, setTab] = useState("convert");
    const [openFaq, setOpenFaq] = useState(null);
    const [spendCoinOpen, setSpendCoinOpen] = useState(false);
    const [spendCoinTab, setSpendCoinTab] = useState("single");
    const [spendCoinFilter, setSpendCoinFilter] = useState("all");
    const [spendCoinSearch, setSpendCoinSearch] = useState("");
    const [spendCoin, setSpendCoin] = useState({ code: "USDT", name: "Tether", icon: "/images/option-img/usdt_icon.svg" });
    const [multiSelectFirstFive, setMultiSelectFirstFive] = useState(false);
    const [receiveCoinOpen, setReceiveCoinOpen] = useState(false);
    // Receive picker uses a simpler modal (no tabs/filters per design).
    const [receiveCoinSearch, setReceiveCoinSearch] = useState("");
    const [receiveCoin, setReceiveCoin] = useState({ code: "BTC", name: "Bitcoin", icon: "/images/market-img/bitcoin_icon.svg" });
    const [addFundsOpen, setAddFundsOpen] = useState(false);
    const [addFundsExpanded, setAddFundsExpanded] = useState(false);
    const [autoFromMenuOpen, setAutoFromMenuOpen] = useState(false);
    const autoFromMenuRef = React.useRef(null);
    const [autoToOpen, setAutoToOpen] = useState(false);
    const [autoToSearch, setAutoToSearch] = useState("");
    const [autoToSelected, setAutoToSelected] = useState(() => [{ code: "BTC", name: "Bitcoin", icon: "/images/market-img/bitcoin_icon.svg" }]);
    const [featuredOpen, setFeaturedOpen] = useState(false);
    const [investAtOpen, setInvestAtOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [autoSubscribe, setAutoSubscribe] = useState(false);
    const [planName] = useState("Portfolio BTC");
    const [planNameOpen, setPlanNameOpen] = useState(false);
    const [planNameDraft, setPlanNameDraft] = useState("Portfolio BTC");
    const investAtWrapRef = React.useRef(null);
    const [isMobileInvestPicker, setIsMobileInvestPicker] = useState(false);
    const [investFrequency, setInvestFrequency] = useState("Hourly");
    const [investStartsAt, setInvestStartsAt] = useState("Hourly");
    const [investTime, setInvestTime] = useState("02:00");

    const toggleFaq = (i) => {
        setOpenFaq((v) => (v === i ? null : i));
    };

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 768px)");
        const sync = () => setIsMobileInvestPicker(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        const investMobileSheetOpen = investAtOpen && isMobileInvestPicker;
        if (!spendCoinOpen && !receiveCoinOpen && !addFundsOpen && !autoToOpen && !featuredOpen && !planNameOpen && !investMobileSheetOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [spendCoinOpen, receiveCoinOpen, addFundsOpen, autoToOpen, featuredOpen, planNameOpen, investAtOpen, isMobileInvestPicker]);

    useEffect(() => {
        if (!investAtOpen || !isMobileInvestPicker) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setInvestAtOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [investAtOpen, isMobileInvestPicker]);

    useEffect(() => {
        if (!autoFromMenuOpen) return;
        const onDown = (e) => {
            if (autoFromMenuRef.current && !autoFromMenuRef.current.contains(e.target)) {
                setAutoFromMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [autoFromMenuOpen]);

    useEffect(() => {
        if (!investAtOpen || isMobileInvestPicker) return;
        const onDown = (e) => {
            if (investAtWrapRef.current && !investAtWrapRef.current.contains(e.target)) {
                setInvestAtOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [investAtOpen, isMobileInvestPicker]);

    useEffect(() => {
        if (!autoToOpen) return;
        setAutoToSearch("");
    }, [autoToOpen]);

    useEffect(() => {
        if (!spendCoinOpen) return;
        setMultiSelectFirstFive(false);
    }, [spendCoinOpen, spendCoinTab]);

    const ALL_COINS = [
        // Static sample set for All (bigger list + scroll)
        { code: "REDO", name: "Resistance Dog", icon: "/images/coin_icon.svg" },
        { code: "DUCK", name: "DuckChain", icon: "/images/coin_icon.svg" },
        { code: "DOGS", name: "DOGS", icon: "/images/coin_icon.svg" },
        { code: "BTC", name: "Bitcoin", icon: "/images/market-img/bitcoin_icon.svg" },
        { code: "ETH", name: "Ethereum", icon: "/images/convert-img/ethereum.svg" },
        { code: "USDT", name: "Tether", icon: "/images/option-img/usdt_icon.svg" },
        { code: "USDC", name: "USD Coin", icon: "/images/convert-img/usd_coin.svg" },
        { code: "XRP", name: "XRP", icon: "/images/convert-img/xrp_icon.svg" },
        { code: "SOL", name: "Solana", icon: "/images/convert-img/solana_icon.svg" },
        { code: "DOGE", name: "Dogecoin", icon: "/images/convert-img/doge_icon.svg" },
        { code: "BNB", name: "BNB", icon: "/images/AnnouncementImg/bnb_icon.svg" },
        { code: "TRX", name: "Tron", icon: "/images/convert-img/tron_icon.svg" },
        { code: "LTC", name: "Litecoin", icon: "/images/convert-img/litecoin_icon.svg" },
        { code: "ADA", name: "Cardano", icon: "/images/coin_icon.svg" },
        { code: "AVAX", name: "Avalanche", icon: "/images/coin_icon.svg" },
        { code: "DOT", name: "Polkadot", icon: "/images/coin_icon.svg" },
        { code: "MATIC", name: "Polygon", icon: "/images/coin_icon.svg" },
        { code: "ATOM", name: "Cosmos", icon: "/images/coin_icon.svg" },
        { code: "UNI", name: "Uniswap", icon: "/images/coin_icon.svg" },
        { code: "AAVE", name: "Aave", icon: "/images/coin_icon.svg" },
        { code: "SUSHI", name: "SushiSwap", icon: "/images/coin_icon.svg" },
        { code: "FIL", name: "Filecoin", icon: "/images/coin_icon.svg" },
        { code: "NEAR", name: "NEAR", icon: "/images/coin_icon.svg" },
    ];

    const ALPHA_COINS = [
        { code: "PALS", name: "PALS", icon: "/images/coin_icon.svg", alpha: true },
        { code: "LINK", name: "LINK", icon: "/images/coin_icon.svg", alpha: true },
        { code: "USD1", name: "USD1", icon: "/images/coin_icon.svg", alpha: true },
        { code: "REDO", name: "Resistance Dog", icon: "/images/coin_icon.svg", alpha: true },
        { code: "DUCK", name: "DuckChain", icon: "/images/coin_icon.svg", alpha: true },
        { code: "CHEQ", name: "CHEQD NETWORK", icon: "/images/coin_icon.svg", alpha: true },
        { code: "TKO", name: "Tokocrypto", icon: "/images/coin_icon.svg", alpha: true },
        { code: "ARTH", name: "Artha", icon: "/images/coin_icon.svg", alpha: true },
        { code: "NOVA", name: "Nova", icon: "/images/coin_icon.svg", alpha: true },
        { code: "ZETA", name: "Zeta", icon: "/images/coin_icon.svg", alpha: true },
        { code: "KITE", name: "Kite", icon: "/images/coin_icon.svg", alpha: true },
        { code: "WAVE", name: "Wave", icon: "/images/coin_icon.svg", alpha: true },
        { code: "MOON", name: "Moon", icon: "/images/coin_icon.svg", alpha: true },
        { code: "STAR", name: "Star", icon: "/images/coin_icon.svg", alpha: true },
        { code: "BIRD", name: "Bird", icon: "/images/coin_icon.svg", alpha: true },
    ];

    const baseCoins = spendCoinFilter === "alpha" ? ALPHA_COINS : ALL_COINS;
    const filteredCoins = baseCoins.filter((c) => {
        const q = spendCoinSearch.trim().toLowerCase();
        if (!q) return true;
        return c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
    });

    const filteredReceiveCoins = ALL_COINS.filter((c) => {
        const q = receiveCoinSearch.trim().toLowerCase();
        if (!q) return true;
        return c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
    });

    const AUTO_TO_COINS = [
        { code: "ETH", name: "Ethereum", icon: "/images/convert-img/ethereum.svg" },
        { code: "GT", name: "GT", icon: "/images/coin_icon.svg" },
        { code: "SOL", name: "Solana", icon: "/images/convert-img/solana_icon.svg" },
        { code: "GRIN", name: "GRIN", icon: "/images/coin_icon.svg" },
        { code: "XAUT", name: "XAUT", icon: "/images/coin_icon.svg" },
        { code: "HYPE", name: "HYPE", icon: "/images/coin_icon.svg" },
        { code: "BTC", name: "Bitcoin", icon: "/images/market-img/bitcoin_icon.svg" },
        { code: "USDT", name: "Tether", icon: "/images/option-img/usdt_icon.svg" },
    ];

    const autoToFiltered = AUTO_TO_COINS.filter((c) => {
        const q = autoToSearch.trim().toLowerCase();
        if (!q) return true;
        return c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
    });

    const autoToCount = autoToSelected.length;
    const autoToMax = 10;

    const INVEST_FREQUENCIES = ["Hourly", "Daily", "Weekly", "Biweekly", "Monthly"];

    const INVEST_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const INVEST_MONTH_DAYS = Array.from({ length: 31 }, (_, i) => {
        const day = i + 1;
        const mod100 = day % 100;
        const suffix = mod100 >= 11 && mod100 <= 13 ? "th" : day % 10 === 1 ? "st" : day % 10 === 2 ? "nd" : day % 10 === 3 ? "rd" : "th";
        return `${day}${suffix}`;
    });

    const INVEST_STARTS_AT_MAP = {
        Hourly: ["Every hour", "Every 4 hours"],
        Daily: ["Everyday"],
        Weekly: INVEST_DAYS,
        Biweekly: INVEST_DAYS,
        Monthly: INVEST_MONTH_DAYS,
    };

    const INVEST_TIMES = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);

    const FEATURED_PORTFOLIOS = [
        {
            id: "stablecoin",
            title: "Stablecoin Ecosystem",
            copiers: "1,804",
            tags: ["Stablecoin Ecosystem", "Payment Network", "DeFi"],
            icons: ["/images/option-img/usdt_icon.svg", "/images/convert-img/usd_coin.svg", "/images/convert-img/tron_icon.svg"],
        },
        {
            id: "dualcore",
            title: "Dual-core Market Overview",
            copiers: "1,318",
            tags: ["Mainstream Assets", "Long-term allocation", "High Liquidity"],
            icons: ["/images/market-img/bitcoin_icon.svg", "/images/convert-img/ethereum.svg"],
        },
        {
            id: "topfive",
            title: "Top Five Leaders",
            copiers: "1,217",
            tags: ["Diversification", "Growth potential", "High Liquidity"],
            icons: ["/images/market-img/bitcoin_icon.svg", "/images/convert-img/ethereum.svg", "/images/convert-img/solana_icon.svg", "/images/AnnouncementImg/bnb_icon.svg"],
        },
        {
            id: "eth",
            title: "ETH Ecosystem",
            copiers: "1,102",
            tags: ["Ecosystem", "Layer 2", "DeFi"],
            icons: ["/images/convert-img/ethereum.svg", "/images/coin_icon.svg", "/images/coin_icon.svg"],
        },
    ];

    const ADD_FUNDS_ITEMS = [
        {
            id: "transfer",
            title: "Transfer",
            desc: "Transfer assets among different accounts",
            meta: `Transferable: 0 ${spendCoin.code}`,
            icon: "ri-arrow-left-right-line",
        },
        {
            id: "onchain",
            title: "Onchain Deposit",
            desc: "Deposit crypto from other exchanges/wallets to AGCX",
            meta: "",
            icon: "ri-download-cloud-2-line",
        },
        {
            id: "p2p",
            title: "P2P Trading",
            desc: "Zero fees, 400+ payment options, and seamless cryptocurrency buying & selling",
            meta: "",
            icon: "ri-exchange-2-line",
        },
        {
            id: "buyusd",
            title: "Buy with USD",
            desc: "Buy and sell crypto via Apple Pay, cards, Google Pay, bank transfers, and more",
            meta: "",
            icon: "ri-global-line",
        },
        {
            id: "redeem",
            title: "Redeem AGCXCode",
            desc: "For asset transfer among AGCX accounts only",
            meta: "",
            icon: "ri-barcode-box-line",
        },
    ];
    const addFundsVisible = addFundsExpanded ? ADD_FUNDS_ITEMS : ADD_FUNDS_ITEMS.slice(0, 3);

    const renderInvestSchedulePicker = (forMobileSheet) => (
        <div
            className={`convert-page__investPopover${forMobileSheet ? " convert-page__investPopover--mobileSheet" : ""}`}
            role="listbox"
            aria-label="Invest schedule"
        >
            <div className="convert-page__investCols">
                <div className="convert-page__investCol">
                    <div className="convert-page__investColHead">Frequency</div>
                    {INVEST_FREQUENCIES.map((f) => (
                        <button
                            key={f}
                            type="button"
                            className="convert-page__investRow"
                            onClick={() => {
                                setInvestFrequency(f);
                                const starts = INVEST_STARTS_AT_MAP[f] || [f];
                                setInvestStartsAt(starts[0]);
                            }}
                        >
                            <span className="convert-page__investRowText">{f}</span>
                            {investFrequency === f ? <span className="convert-page__investCheck" aria-hidden>✓</span> : null}
                        </button>
                    ))}
                </div>

                <div className="convert-page__investCol">
                    <div className="convert-page__investColHead">Starts At</div>
                    {(INVEST_STARTS_AT_MAP[investFrequency] || [investFrequency]).map((s) => (
                        <button key={s} type="button" className="convert-page__investRow" onClick={() => setInvestStartsAt(s)}>
                            <span className="convert-page__investRowText">{s}</span>
                            {investStartsAt === s ? <span className="convert-page__investCheck" aria-hidden>✓</span> : null}
                        </button>
                    ))}
                </div>

                <div className="convert-page__investCol">
                    <div className="convert-page__investColHead">Time</div>
                    {INVEST_TIMES.map((t) => (
                        <button
                            key={t}
                            type="button"
                            className="convert-page__investRow"
                            onClick={() => {
                                setInvestTime(t);
                                setInvestAtOpen(false);
                            }}
                        >
                            <span className="convert-page__investRowText">{t}</span>
                            {investTime === t ? <span className="convert-page__investCheck" aria-hidden>✓</span> : null}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="convert-page">
            <section className="convert-page__hero" aria-labelledby="convert-page-hero-title">
                <div className="convert-page__heroInner">
                    <div className="convert-page__heroCol convert-page__heroCol--text">
                        <h1 id="convert-page-hero-title" className="convert-page__title">
                            Convert <span className="convert-page__titleToken convert-page__titleToken--usdt">USDT</span> to{" "}
                            <span className="convert-page__titleToken convert-page__titleToken--btc">BTC</span>
                        </h1>
                        <p className="convert-page__lead">
                            Instantly convert USDT to BTC with real-time market rates, delivering a seamless, secure, and hassle-free trading experience
                            powered by deep liquidity, zero hidden fees, and lightning-fast execution—designed to handle
                            everything from small transactions to large-volume trades with maximum efficiency and precision.
                        </p>
                    </div>

                    <div className="convert-page__heroCol convert-page__heroCol--card">
                        <div className="convert-page__card">
                            <div className="convert-page__cardHead">
                                <div className="convert-page__tabs" role="tablist">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={tab === "convert"}
                                        className={`convert-page__tab${tab === "convert" ? " convert-page__tab--active" : ""}`}
                                        onClick={() => setTab("convert")}
                                    >
                                        Convert
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={tab === "autoinvest"}
                                        className={`convert-page__tab${tab === "autoinvest" ? " convert-page__tab--active" : ""}`}
                                        onClick={() => setTab("autoinvest")}
                                    >
                                        Auto-Invest <span className="convert-page__tabEmoji">🔥</span>
                                    </button>
                                </div>
                                <Link
                                    to="/user_profile/orders/convert_orders"
                                    className="convert-page__historyBtn"
                                    title="History"
                                    aria-label="Convert history"
                                >
                                    <img src="/images/file-vector.svg" alt="file" />
                                </Link>
                            </div>

                            {featuredOpen && typeof document !== "undefined"
                                ? createPortal(
                                      <div
                                          className="convert-page__coinModalOverlay"
                                          role="presentation"
                                          onMouseDown={(e) => {
                                              if (e.button !== 0) return;
                                              if (e.target === e.currentTarget) setFeaturedOpen(false);
                                          }}
                                      >
                                          <div className="convert-page__featuredModal" role="dialog" aria-modal="true" aria-label="Featured Portfolios">
                                              <div className="convert-page__coinModalHead">
                                                  <h3 className="convert-page__coinModalTitle">Featured Portfolios</h3>
                                                  <button type="button" className="convert-page__coinModalClose" onClick={() => setFeaturedOpen(false)} aria-label="Close">
                                                      ×
                                                  </button>
                                              </div>

                                              <div className="convert-page__featuredBody">
                                                  {FEATURED_PORTFOLIOS.map((p) => (
                                                      <div key={p.id} className="convert-page__featuredCard">
                                                          <div className="convert-page__featuredTop">
                                                              <div className="convert-page__featuredTitleRow">
                                                                  <span className="convert-page__featuredTitle">{p.title}</span>
                                                                  <span className="convert-page__featuredInfo" aria-hidden>
                                                                      i
                                                                  </span>
                                                              </div>
                                                              <button type="button" className="convert-page__featuredCopy">
                                                                  Copy
                                                              </button>
                                                          </div>

                                                          <div className="convert-page__featuredMid">
                                                              <div className="convert-page__featuredMetric">
                                                                  <span className="convert-page__featuredLabel">Number of Copiers</span>
                                                                  <span className="convert-page__featuredValue">{p.copiers}</span>
                                                              </div>
                                                              <div className="convert-page__featuredMetric">
                                                                  <span className="convert-page__featuredLabel">Portfolio</span>
                                                                  <span className="convert-page__featuredIcons">
                                                                      {p.icons.map((src, idx) => (
                                                                          <img key={`${p.id}-${idx}`} className="convert-page__featuredIcon" src={src} alt="" />
                                                                      ))}
                                                                  </span>
                                                              </div>
                                                          </div>

                                                          <div className="convert-page__featuredTags">
                                                              {p.tags.map((t) => (
                                                                  <span key={t} className="convert-page__featuredTag">
                                                                      {t}
                                                                  </span>
                                                              ))}
                                                          </div>
                                                      </div>
                                                  ))}
                                              </div>
                                          </div>
                                      </div>,
                                      document.body
                                  )
                                : null}

                            {addFundsOpen && typeof document !== "undefined"
                                ? createPortal(
                                      <div
                                          className="convert-page__coinModalOverlay"
                                          role="presentation"
                                          onMouseDown={(e) => {
                                              if (e.button !== 0) return;
                                              if (e.target === e.currentTarget) setAddFundsOpen(false);
                                          }}
                                      >
                                          <div className="convert-page__fundsModal" role="dialog" aria-modal="true" aria-label="Add Funds">
                                              <div className="convert-page__coinModalHead">
                                                  <h3 className="convert-page__coinModalTitle">Add Funds</h3>
                                                  <button
                                                      type="button"
                                                      className="convert-page__coinModalClose"
                                                      onClick={() => {
                                                          setAddFundsOpen(false);
                                                          setAddFundsExpanded(false);
                                                      }}
                                                      aria-label="Close"
                                                  >
                                                      ×
                                                  </button>
                                              </div>

                                              <div className="convert-page__fundsBody">
                                                  {addFundsVisible.map((it) => (
                                                      <button key={it.id} type="button" className="convert-page__fundsCard">
                                                          <span className="convert-page__fundsIcon" aria-hidden>
                                                              <i className={it.icon} aria-hidden />
                                                          </span>
                                                          <span className="convert-page__fundsMeta">
                                                              <span className="convert-page__fundsTitle">{it.title}</span>
                                                              <span className="convert-page__fundsDesc">{it.desc}</span>
                                                              {it.meta ? <span className="convert-page__fundsSub">{it.meta}</span> : null}
                                                          </span>
                                                      </button>
                                                  ))}

                                                  <button type="button" className="convert-page__fundsMore" onClick={() => setAddFundsExpanded((v) => !v)}>
                                                      {addFundsExpanded ? "View Less" : "View More"}
                                                  </button>
                                              </div>
                                          </div>
                                      </div>,
                                      document.body
                                  )
                                : null}

                            {tab === "convert" ? (
                                <div className="convert-page__form">


                                    <div className="Convert_mid_btc_del">

                                        <div className="convert-page__ioPanel">
                                            <div className="convert-page__ioTop">
                                                <span className="convert-page__ioCaption">Spend</span>
                                                <div className="convert-page__ioAvail">
                                                    <span>
                                                        Available: <span className="convert-page__availDash">--</span> USDT
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="convert-page__addBalBtn"
                                                        aria-label="Add balance"
                                                        aria-haspopup="dialog"
                                                        aria-expanded={addFundsOpen}
                                                        onClick={() => {
                                                            setSpendCoinOpen(false);
                                                            setReceiveCoinOpen(false);
                                                            setAddFundsExpanded(false);
                                                            setAddFundsOpen(true);
                                                        }}
                                                    >
                                                        <span className="convert-page__addBalCircle">+</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="convert-page__ioMain">
                                                <button type="button" className="convert-page__coinSelect" onClick={() => setSpendCoinOpen(true)} aria-haspopup="dialog" aria-expanded={spendCoinOpen}>
                                                    <img
                                                        src={spendCoin.icon}
                                                        alt=""
                                                        className="convert-page__coin-icon"
                                                        width="32"
                                                        height="32"
                                                    />
                                                    <span className="convert-page__coinTicker">{spendCoin.code}</span>
                                                    <i className="ri-arrow-down-s-line convert-page__caret" aria-hidden />
                                                </button>
                                                <div className="convert-page__amountBlock">
                                                    <input
                                                        type="text"
                                                        className="convert-page__amountInput"
                                                        placeholder="1-20,000,000"
                                                        inputMode="decimal"
                                                        aria-label="Spend amount"
                                                    />
                                                    <button type="button" className="convert-page__maxPill">
                                                        Max
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {spendCoinOpen && typeof document !== "undefined"
                                            ? createPortal(
                                                  <div
                                                      className="convert-page__coinModalOverlay"
                                                      role="presentation"
                                                      onMouseDown={(e) => {
                                                          // Only close on LEFT click on the backdrop.
                                                          if (e.button !== 0) return;
                                                          if (e.target === e.currentTarget) setSpendCoinOpen(false);
                                                      }}
                                                  >
                                                      <div
                                                          className="convert-page__coinModal"
                                                          role="dialog"
                                                          aria-modal="true"
                                                          aria-label="Select Coin"
                                                          onMouseDown={(e) => e.stopPropagation()}
                                                      >
                                                          <div className="convert-page__coinModalHead">
                                                              <h3 className="convert-page__coinModalTitle">Select Coin</h3>
                                                              <button
                                                                  type="button"
                                                                  className="convert-page__coinModalClose"
                                                                  onClick={() => setSpendCoinOpen(false)}
                                                                  aria-label="Close"
                                                              >
                                                                  ×
                                                              </button>
                                                          </div>

                                                          <div className="convert-page__coinModalBody">
                                                              <div className="convert-page__coinModalTabs">
                                                                  <button
                                                                      type="button"
                                                                      className={`convert-page__coinModalTab${spendCoinTab === "single" ? " convert-page__coinModalTab--active" : ""}`}
                                                                      onClick={() => setSpendCoinTab("single")}
                                                                  >
                                                                      Single Coin
                                                                  </button>
                                                                  <button
                                                                      type="button"
                                                                      className={`convert-page__coinModalTab${spendCoinTab === "multi" ? " convert-page__coinModalTab--active" : ""}`}
                                                                      onClick={() => setSpendCoinTab("multi")}
                                                                  >
                                                                      Multi Coins
                                                                  </button>
                                                              </div>

                                                              <div className="convert-page__coinModalSearch">
                                                                  <i className="ri-search-line convert-page__coinModalSearchIcon" aria-hidden />
                                                                  <input
                                                                      type="search"
                                                                      className="convert-page__coinModalSearchInput"
                                                                      placeholder="Search"
                                                                      value={spendCoinSearch}
                                                                      onChange={(e) => setSpendCoinSearch(e.target.value)}
                                                                      autoComplete="off"
                                                                  />
                                                              </div>

                                                              <div className="convert-page__coinModalFilters">
                                                                  <button
                                                                      type="button"
                                                                      className={`convert-page__coinModalFilter${spendCoinFilter === "all" ? " convert-page__coinModalFilter--active" : ""}`}
                                                                      onClick={() => setSpendCoinFilter("all")}
                                                                  >
                                                                      All
                                                                  </button>
                                                                  <button
                                                                      type="button"
                                                                      className={`convert-page__coinModalFilter${spendCoinFilter === "alpha" ? " convert-page__coinModalFilter--active" : ""}`}
                                                                      onClick={() => setSpendCoinFilter("alpha")}
                                                                  >
                                                                      Alpha
                                                                  </button>
                                                              </div>

                                                              <div className="convert-page__coinModalList">
                                                                  {filteredCoins.map((c) => {
                                                                      const active = spendCoin.code === c.code;
                                                                      return (
                                                                          <button
                                                                              key={c.code}
                                                                              type="button"
                                                                              className={`convert-page__coinRow${active ? " convert-page__coinRow--active" : ""}`}
                                                                              onClick={() => {
                                                                                  setSpendCoin(c);
                                                                                  setSpendCoinOpen(false);
                                                                              }}
                                                                          >
                                                                              <img className="convert-page__coinRowIcon" src={c.icon} alt="" />
                                                                              <span className="convert-page__coinRowMeta">
                                                                                  <span className="convert-page__coinRowTop">
                                                                                      <span className="convert-page__coinRowCode">{c.code}</span>
                                                                                      {c.alpha ? <span className="convert-page__coinRowBadge">Alpha</span> : null}
                                                                                  </span>
                                                                                  <span className="convert-page__coinRowName">{c.name}</span>
                                                                              </span>
                                                                          </button>
                                                                      );
                                                                  })}
                                                              </div>

                                                              {spendCoinTab === "multi" ? (
                                                                  <div className="convert-page__coinModalFoot">
                                                                      <label className="convert-page__coinModalCheck">
                                                                          <input
                                                                              type="checkbox"
                                                                              checked={multiSelectFirstFive}
                                                                              onChange={(e) => setMultiSelectFirstFive(e.target.checked)}
                                                                          />
                                                                          <span>Select the first 5 coins</span>
                                                                      </label>
                                                                      <button type="button" className="convert-page__coinModalConfirm" disabled={!multiSelectFirstFive}>
                                                                          Confirm
                                                                      </button>
                                                                  </div>
                                                              ) : null}
                                                          </div>
                                                      </div>
                                                  </div>,
                                                  document.body
                                              )
                                            : null}

                                        <div className="convert-page__swapMid">
                                            <button type="button" className="convert-page__swapBtn" aria-label="Swap currencies">
                                    <img src="/images/usdt_arrow2.svg" alt="swap" />
                                            </button>
                                        </div>

                                        <div className="convert-page__ioPanel">
                                            <div className="convert-page__ioTop">
                                                <span className="convert-page__ioCaption">Receive</span>
                                                <div className="convert-page__ioAvail">
                                                    <span>
                                                        Available: <span className="convert-page__availDash">--</span> BTC
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="convert-page__ioMain">
                                                <button
                                                    type="button"
                                                    className="convert-page__coinSelect"
                                                    onClick={() => setReceiveCoinOpen(true)}
                                                    aria-haspopup="dialog"
                                                    aria-expanded={receiveCoinOpen}
                                                >
                                                    <img
                                                        src={receiveCoin.icon}
                                                        alt=""
                                                        className="convert-page__coin-icon"
                                                        width="32"
                                                        height="32"
                                                    />
                                                    <span className="convert-page__coinTicker">{receiveCoin.code}</span>
                                                    <i className="ri-arrow-down-s-line convert-page__caret" aria-hidden />
                                                </button>
                                                <div className="convert-page__amountBlock convert-page__amountBlock--receive">
                                                    <input
                                                        type="text"
                                                        className="convert-page__amountInput"
                                                        placeholder="0.000013-250"
                                                        inputMode="decimal"
                                                        aria-label="Receive amount"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {receiveCoinOpen && typeof document !== "undefined"
                                            ? createPortal(
                                                  <div
                                                      className="convert-page__coinModalOverlay"
                                                      role="presentation"
                                                      onMouseDown={(e) => {
                                                          // Only close on LEFT click on the backdrop.
                                                          if (e.button !== 0) return;
                                                          if (e.target === e.currentTarget) setReceiveCoinOpen(false);
                                                      }}
                                                  >
                                                      <div
                                                          className="convert-page__coinModal"
                                                          role="dialog"
                                                          aria-modal="true"
                                                          aria-label="Select Coin"
                                                          onMouseDown={(e) => e.stopPropagation()}
                                                      >
                                                          <div className="convert-page__coinModalHead">
                                                              <h3 className="convert-page__coinModalTitle">Select Coin</h3>
                                                              <button
                                                                  type="button"
                                                                  className="convert-page__coinModalClose"
                                                                  onClick={() => setReceiveCoinOpen(false)}
                                                                  aria-label="Close"
                                                              >
                                                                  ×
                                                              </button>
                                                          </div>

                                                          <div className="convert-page__coinModalBody">
                                                              <div className="convert-page__coinModalSearch">
                                                                  <i className="ri-search-line convert-page__coinModalSearchIcon" aria-hidden />
                                                                  <input
                                                                      type="search"
                                                                      className="convert-page__coinModalSearchInput"
                                                                      placeholder="Search"
                                                                      value={receiveCoinSearch}
                                                                      onChange={(e) => setReceiveCoinSearch(e.target.value)}
                                                                      autoComplete="off"
                                                                  />
                                                              </div>

                                                              <div className="convert-page__coinModalList">
                                                                  {filteredReceiveCoins.map((c) => {
                                                                      const active = receiveCoin.code === c.code;
                                                                      return (
                                                                          <button
                                                                              key={c.code}
                                                                              type="button"
                                                                              className={`convert-page__coinRow${active ? " convert-page__coinRow--active" : ""}`}
                                                                              onClick={() => {
                                                                                  setReceiveCoin(c);
                                                                                  setReceiveCoinOpen(false);
                                                                              }}
                                                                          >
                                                                              <img className="convert-page__coinRowIcon" src={c.icon} alt="" />
                                                                              <span className="convert-page__coinRowMeta">
                                                                                  <span className="convert-page__coinRowTop">
                                                                                      <span className="convert-page__coinRowCode">{c.code}</span>
                                                                                  </span>
                                                                                  <span className="convert-page__coinRowName">{c.name}</span>
                                                                              </span>
                                                                          </button>
                                                                      );
                                                                  })}
                                                              </div>
                                                          </div>
                                                      </div>
                                                  </div>,
                                                  document.body
                                              )
                                            : null}

                                    </div>

                                    <p className="convert-page__rate">Convert Rate: 1 BTC ≈ -- USDT</p>
                                    <Link to="/login" className="convert-page__cta">
                                        Log In
                                    </Link>
                                </div>
                            ) : (
                                <div className="convert-page__form">
                                    <div className="Convert_mid_btc_del">
                                        <div className="convert-page__ioPanel">
                                            <div className="convert-page__ioTop">
                                                <span className="convert-page__ioCaption">From</span>
                                                <div className="convert-page__ioAvail">
                                                    <span>
                                                        Available: <span className="convert-page__availDash">0</span> {spendCoin.code}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="convert-page__addBalBtn"
                                                        aria-label="Add balance"
                                                        aria-haspopup="dialog"
                                                        aria-expanded={addFundsOpen}
                                                        onClick={() => {
                                                            setSpendCoinOpen(false);
                                                            setReceiveCoinOpen(false);
                                                            setAddFundsExpanded(false);
                                                            setAddFundsOpen(true);
                                                        }}
                                                    >
                                                        <span className="convert-page__addBalCircle">+</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="convert-page__ioMain">
                                                <div className="convert-page__autoCoinWrap" ref={autoFromMenuRef}>
                                                    <button
                                                        type="button"
                                                        className="convert-page__coinSelect"
                                                        onClick={() => setAutoFromMenuOpen((v) => !v)}
                                                        aria-haspopup="listbox"
                                                        aria-expanded={autoFromMenuOpen}
                                                    >
                                                        <img src={spendCoin.icon} alt="" className="convert-page__coin-icon" width="32" height="32" />
                                                        <span className="convert-page__coinTicker">{spendCoin.code}</span>
                                                        <i className="ri-arrow-down-s-line convert-page__caret" aria-hidden />
                                                    </button>

                                                    {autoFromMenuOpen ? (
                                                        <div className="convert-page__autoCoinMenu" role="listbox" aria-label="Select coin">
                                                            {[
                                                                { code: "BTC", name: "Bitcoin", icon: "/images/market-img/bitcoin_icon.svg" },
                                                                { code: "USDT", name: "Tether", icon: "/images/option-img/usdt_icon.svg" },
                                                            ].map((c) => {
                                                                const active = spendCoin.code === c.code;
                                                                return (
                                                                    <button
                                                                        key={c.code}
                                                                        type="button"
                                                                        className={`convert-page__autoCoinItem${active ? " convert-page__autoCoinItem--active" : ""}`}
                                                                        role="option"
                                                                        aria-selected={active}
                                                                        onClick={() => {
                                                                            setSpendCoin({ ...c });
                                                                            setAutoFromMenuOpen(false);
                                                                        }}
                                                                    >
                                                                        <span className="convert-page__autoCoinLeft">
                                                                            <img className="convert-page__coin-icon" src={c.icon} alt="" width="28" height="28" />
                                                                            <span className="convert-page__autoCoinMeta">
                                                                                <span className="convert-page__autoCoinCode">{c.code}</span>
                                                                                <span className="convert-page__autoCoinBal">0</span>
                                                                            </span>
                                                                        </span>
                                                                        {active ? <span className="convert-page__autoCoinCheck" aria-hidden>✓</span> : null}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="convert-page__amountBlock">
                                                    <input
                                                        type="text"
                                                        className="convert-page__amountInput"
                                                        placeholder="≥3"
                                                        inputMode="decimal"
                                                        aria-label="From amount"
                                                    />
                                                    <button type="button" className="convert-page__maxPill">
                                                        Max
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="convert-page__ioPanel">
                                            <div className="convert-page__ioTop">
                                                <span className="convert-page__ioCaption">To</span>
                                                <div className="convert-page__ioAvail">
                                                    <span className="convert-page__autoTopRight">100% / 100%</span>
                                                </div>
                                            </div>
                                            <div className="convert-page__ioMain">
                                                <button type="button" className="convert-page__coinSelect" onClick={() => setAutoToOpen(true)} aria-haspopup="dialog" aria-expanded={autoToOpen}>
                                                    <img src={receiveCoin.icon} alt="" className="convert-page__coin-icon" width="32" height="32" />
                                                    <span className="convert-page__coinTicker">{receiveCoin.code}</span>
                                                    <i className="ri-arrow-down-s-line convert-page__caret" aria-hidden />
                                                </button>
                                                <div className="convert-page__amountBlock convert-page__amountBlock--receive">
                                                    <div className="convert-page__autoBigPct">100%</div>
                                                </div>
                                            </div>
                                        </div>

                                        {autoToOpen && typeof document !== "undefined"
                                            ? createPortal(
                                                  <div
                                                      className="convert-page__coinModalOverlay"
                                                      role="presentation"
                                                      onMouseDown={(e) => {
                                                          if (e.button !== 0) return;
                                                          if (e.target === e.currentTarget) setAutoToOpen(false);
                                                      }}
                                                  >
                                                      <div className="convert-page__chooseModal" role="dialog" aria-modal="true" aria-label="Choose Coin">
                                                          <div className="convert-page__coinModalHead">
                                                              <h3 className="convert-page__coinModalTitle">Choose Coin</h3>
                                                              <button type="button" className="convert-page__coinModalClose" onClick={() => setAutoToOpen(false)} aria-label="Close">
                                                                  ×
                                                              </button>
                                                          </div>

                                                          <div className="convert-page__chooseBody">
                                                              <button
                                                                  type="button"
                                                                  className="convert-page__chooseBanner"
                                                                  onClick={() => setFeaturedOpen(true)}
                                                              >
                                                                  <span className="convert-page__chooseBannerLeft">
                                                                      <span className="convert-page__chooseFlame" aria-hidden>🔥</span>
                                                                      <span>Not sure what coin to invest in? Try Featured Portfolios.</span>
                                                                  </span>
                                                                  <i className="ri-arrow-right-s-line" aria-hidden />
                                                              </button>

                                                              <div className="convert-page__coinModalSearch">
                                                                  <i className="ri-search-line convert-page__coinModalSearchIcon" aria-hidden />
                                                                  <input
                                                                      type="search"
                                                                      className="convert-page__coinModalSearchInput"
                                                                      placeholder="Search"
                                                                      value={autoToSearch}
                                                                      onChange={(e) => setAutoToSearch(e.target.value)}
                                                                      autoComplete="off"
                                                                  />
                                                              </div>

                                                              <div className="convert-page__chooseList">
                                                                  {autoToFiltered.map((c) => {
                                                                      const selected = autoToSelected.some((s) => s.code === c.code);
                                                                      return (
                                                                          <button
                                                                              key={c.code}
                                                                              type="button"
                                                                              className={`convert-page__chooseRow${selected ? " convert-page__chooseRow--selected" : ""}`}
                                                                              onClick={() => {
                                                                                  setAutoToSelected((prev) => {
                                                                                      const exists = prev.some((p) => p.code === c.code);
                                                                                      if (exists) return prev.filter((p) => p.code !== c.code);
                                                                                      if (prev.length >= autoToMax) return prev;
                                                                                      return [...prev, c];
                                                                                  });
                                                                              }}
                                                                          >
                                                                              <span className="convert-page__chooseLeft">
                                                                                  <img className="convert-page__coinRowIcon" src={c.icon} alt="" />
                                                                                  <span className="convert-page__coinRowMeta">
                                                                                      <span className="convert-page__coinRowCode">{c.code}</span>
                                                                                      <span className="convert-page__coinRowName">{c.name}</span>
                                                                                  </span>
                                                                              </span>
                                                                              <span className={`convert-page__chooseBox${selected ? " convert-page__chooseBox--on" : ""}`} aria-hidden />
                                                                          </button>
                                                                      );
                                                                  })}
                                                              </div>
                                                          </div>

                                                          <div className="convert-page__chooseFoot">
                                                              <div className="convert-page__chooseChips">
                                                                  {autoToSelected.map((c) => (
                                                                      <button
                                                                          key={c.code}
                                                                          type="button"
                                                                          className="convert-page__chooseChip"
                                                                          onClick={() => setAutoToSelected((p) => p.filter((x) => x.code !== c.code))}
                                                                      >
                                                                          {c.code} <span aria-hidden>×</span>
                                                                      </button>
                                                                  ))}
                                                              </div>
                                                              <div className="convert-page__chooseActions">
                                                                  <div className="convert-page__chooseCount">
                                                                      ({autoToCount} / {autoToMax})
                                                                  </div>
                                                                  <button
                                                                      type="button"
                                                                      className="convert-page__chooseConfirm"
                                                                      disabled={autoToCount === 0}
                                                                      onClick={() => {
                                                                          setReceiveCoin(autoToSelected[0] || receiveCoin);
                                                                          setAutoToOpen(false);
                                                                      }}
                                                                  >
                                                                      Confirm
                                                                  </button>
                                                              </div>
                                                          </div>
                                                      </div>
                                                  </div>,
                                                  document.body
                                              )
                                            : null}

                                        <div className="convert-page__ioPanel convert-page__ioPanel--compact">
                                            <div className="convert-page__ioTop" ref={investAtWrapRef}>
                                                <span className="convert-page__ioCaption">Invest At</span>
                                                <button
                                                    type="button"
                                                    className="convert-page__autoSelect"
                                                    aria-haspopup="listbox"
                                                    aria-expanded={investAtOpen}
                                                    onClick={() => setInvestAtOpen((v) => !v)}
                                                >
                                                    {investFrequency} {investTime} <i className="ri-arrow-down-s-line convert-page__caret" aria-hidden />
                                                </button>

                                                {investAtOpen && !isMobileInvestPicker ? renderInvestSchedulePicker(false) : null}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="convert-page__autoAdvanced"
                                            aria-expanded={advancedOpen}
                                            onClick={() => setAdvancedOpen((v) => !v)}
                                        >
                                            <span>Advanced</span>
                                            <i className={`ri-arrow-down-s-line convert-page__caret${advancedOpen ? " convert-page__caret--up" : ""}`} aria-hidden />
                                        </button>

                                        {advancedOpen ? (
                                            <div className="convert-page__advancedPanel" role="region" aria-label="Advanced settings">
                                                <div className="convert-page__advancedRow">
                                                    <span className="convert-page__advancedLabel">Auto-Subscribe Simple Earn</span>
                                                    <button
                                                        type="button"
                                                        className={`convert-page__switch${autoSubscribe ? " convert-page__switch--on" : ""}`}
                                                        role="switch"
                                                        aria-checked={autoSubscribe}
                                                        onClick={() => setAutoSubscribe((v) => !v)}
                                                    >
                                                        <span className="convert-page__switchThumb" aria-hidden />
                                                    </button>
                                                </div>

                                                <div className="convert-page__advancedRow">
                                                    <span className="convert-page__advancedLabel">Plan Name</span>
                                                    <button
                                                        type="button"
                                                        className="convert-page__advancedValueBtn"
                                                        onClick={() => {
                                                            setPlanNameDraft(planName);
                                                            setPlanNameOpen(true);
                                                        }}
                                                        aria-haspopup="dialog"
                                                        aria-expanded={planNameOpen}
                                                        aria-label="Edit plan name"
                                                    >
                                                        <span className="convert-page__advancedValue">{planName}</span>
                                                        <i className="ri-pencil-fill convert-page__advancedIcon" aria-hidden />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}

                                        {planNameOpen && typeof document !== "undefined"
                                            ? createPortal(
                                                  <div
                                                      className="convert-page__planModalOverlay"
                                                      role="presentation"
                                                      onMouseDown={(e) => {
                                                          if (e.button !== 0) return;
                                                          if (e.target === e.currentTarget) setPlanNameOpen(false);
                                                      }}
                                                  >
                                                      <div className="convert-page__planModal" role="dialog" aria-modal="true" aria-label="Plan Name">
                                                          <div className="convert-page__planModalHead">
                                                              <h3 className="convert-page__planModalTitle">Plan Name</h3>
                                                              <button type="button" className="convert-page__planModalClose" onClick={() => setPlanNameOpen(false)} aria-label="Close">
                                                                  ×
                                                              </button>
                                                          </div>

                                                          <div className="convert-page__planModalBody">
                                                              <input
                                                                  type="text"
                                                                  className="convert-page__planModalInput"
                                                                  value={planNameDraft}
                                                                  onChange={(e) => setPlanNameDraft(e.target.value)}
                                                                  autoComplete="off"
                                                              />
                                                          </div>

                                                          <div className="convert-page__planModalFoot">
                                                              <button type="button" className="convert-page__planBtn convert-page__planBtn--ghost" onClick={() => setPlanNameOpen(false)}>
                                                                  Cancel
                                                              </button>
                                                              <button
                                                                  type="button"
                                                                  className="convert-page__planBtn convert-page__planBtn--primary"
                                                                  onClick={() => {
                                                                      // Static demo: keep label, close modal (no persistence requested).
                                                                      setPlanNameOpen(false);
                                                                  }}
                                                              >
                                                                  Confirm
                                                              </button>
                                                          </div>
                                                      </div>
                                                  </div>,
                                                  document.body
                                              )
                                            : null}

                                        <button type="button" className="convert-page__cta" disabled>
                                            Auto Invest
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="convert-page__assetsSection" aria-labelledby="convert-page-assets-title">
                <h2 id="convert-page-assets-title" className="convert-page__sectionTitle">
                    Supported Assets
                </h2>
                <p className="convert-page__sectionSub">11+ cryptocurrencies to choose from. Get the best crypto at AGCX.</p>
                <div className="convert-page__assetGrid">
                    {ASSETS.map((a) => (
                        <div key={a.sym} className={`convert-page__assetCard${a.more ? " convert-page__assetCard--more" : ""}`}>
                            <AssetIcon asset={a} />
                            <div className="convert-page__assetMeta">
                                <span className="convert-page__assetName">{a.name}</span>
                                {/* <span className="convert-page__assetSym">{a.sym}</span> */}
                                {!a.more ? <span className="convert-page__assetHint">{a.hint}</span> : <span className="convert-page__assetHint">{a.hint}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="convert-page__faqSection" aria-labelledby="convert-page-faq-title">
                <h2 id="convert-page-faq-title" className="convert-page__faqTitle">
                    FAQ
                </h2>
                <ul className="convert-page__faqList">
                    {FAQ_ITEMS.map((item, i) => (
                        <li key={item.question} className="convert-page__faqItem">
                            <button type="button" className="convert-page__faqBtn" onClick={() => toggleFaq(i)} aria-expanded={openFaq === i}>
                                <span>{item.question}</span>
                                <i className={`ri-arrow-down-s-line convert-page__faqChev${openFaq === i ? " convert-page__faqChev--open" : ""}`} aria-hidden />
                            </button>
                            {openFaq === i ? (
                                <div className="convert-page__faqPanel" role="region">
                                    <p>{item.answer}</p>
                                </div>
                            ) : null}
                        </li>
                    ))}
                </ul>
            </section>

            {investAtOpen && isMobileInvestPicker && typeof document !== "undefined"
                ? createPortal(
                      <div
                          className="convert-page__investSheetOverlay"
                          role="presentation"
                          onMouseDown={(e) => {
                              if (e.button !== 0) return;
                              if (e.target === e.currentTarget) setInvestAtOpen(false);
                          }}
                      >
                          <div className="convert-page__investSheet" role="dialog" aria-modal="true" aria-label="Invest schedule">
                              <div className="convert-page__investSheetHandle" aria-hidden />
                              {renderInvestSchedulePicker(true)}
                          </div>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
}
